import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, Box, Typography, CircularProgress } from '@mui/material';
import { Person } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../../../../../app/hooks';
import { updateTicketAssignment } from '../../../../state';
import { searchGraphUsers, GraphUser } from '../../../../../../utils/MsGraphApiCall';

// Builds a placeholder GraphUser from a stored assignment, tolerating legacy records that
// only ever persisted one of assignedTo/assignedToName.
const buildPlaceholderUser = (assignedTo: string, assignedToName: string): GraphUser => ({
  id: assignedTo,
  displayName: assignedToName || assignedTo,
  mail: (assignedToName || assignedTo.includes('@')) ? assignedTo : '',
  userPrincipalName: assignedTo,
  accountEnabled: true
});

// AssignedTo is the canonical, unique identifier (email); AssignedToName is display-only.
const resolveAssignmentPayload = (user: GraphUser | null) => user
  ? { assignedTo: user.mail || user.displayName, assignedToName: user.displayName || user.mail }
  : { assignedTo: '', assignedToName: '' };

const AssignedToEdit: React.FC = () => {
  const dispatch = useAppDispatch();
  const { currentTicket } = useAppSelector(state => state.ticket);
  const [users, setUsers] = useState<GraphUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<GraphUser | null>(null);

  // Initialize selected user if there's an existing assigned value
  useEffect(() => {
    const assignedTo = currentTicket?.assignment.assignedTo;
    if (assignedTo && !selectedUser) {
      setSelectedUser(buildPlaceholderUser(assignedTo, currentTicket.assignment.assignedToName));
    }
  }, [currentTicket?.assignment.assignedTo, currentTicket?.assignment.assignedToName, selectedUser]);


  // Debounced search function
  useEffect(() => {
    if (searchQuery.length < 2) {
      setUsers([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setLoading(true);
      try {
        const searchResults = await searchGraphUsers(searchQuery);
        setUsers(searchResults);
      } catch {
        setUsers([]);
      } finally {
        setLoading(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleUserSelect = (_event: React.SyntheticEvent, user: GraphUser | null) => {
    setSelectedUser(user);
    dispatch(updateTicketAssignment(resolveAssignmentPayload(user)));
  };

  const handleInputChange = (_event: React.SyntheticEvent, newInputValue: string) => {
    setSearchQuery(newInputValue);
  };

  return (
    <Autocomplete
      size="small"
      fullWidth
      options={users}
      loading={loading}
      value={selectedUser}
      onChange={handleUserSelect}
      onInputChange={handleInputChange}
      getOptionLabel={(option) => option.displayName || option.mail || ''}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      noOptionsText={searchQuery.length < 2 ? "Type to search users..." : "No users found"}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Assigned To"
          placeholder="Search for a user..."
          InputProps={{
            ...params.InputProps,
            startAdornment: <Person sx={{ mr: 1, color: 'action.active' }} />,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Person sx={{ mr: 1, color: 'action.active' }} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {option.displayName}
              </Typography>
              {option.mail && (
                <Typography variant="caption" color="text.secondary">
                  {option.mail}
                </Typography>
              )}
              {option.jobTitle && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {option.jobTitle}
                  {option.department && ` • ${option.department}`}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}
    />
  );
};

export default AssignedToEdit;
